import { useEffect, useState, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/base/ScrollToTop';
import Breadcrumb, { type BreadcrumbItem } from '@/components/base/Breadcrumb';
import ArticleCard from '@/components/base/ArticleCard';
import { categories, getCategoryBySlug } from '@/mocks/categories';
import { getArticlesByCategory, articles as allArticles } from '@/mocks/articles';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const { t } = useTranslation();
  const [activeSlug, setActiveSlug] = useState(slug || 'all');

  useEffect(() => {
    if (slug) setActiveSlug(slug);
  }, [slug]);

  useEffect(() => {
    const category = getCategoryBySlug(activeSlug);
    const title = category ? `${category.name} - 韩影精选` : '分类浏览 - 韩影精选';
    document.title = title;
  }, [activeSlug]);

  const displayArticles = useMemo(() => {
    if (activeSlug === 'all') return allArticles;
    return getArticlesByCategory(activeSlug);
  }, [activeSlug]);

  const currentCategory = getCategoryBySlug(activeSlug);

  const breadcrumbItems: BreadcrumbItem[] = [
    { label: t('breadcrumb.home'), href: '/' },
    { label: t('nav.category') },
    ...(currentCategory ? [{ label: currentCategory.name }] : []),
  ];

  const allTabs = [
    { slug: 'all', name: t('category.all'), count: allArticles.length },
    ...categories.map((c) => ({ slug: c.slug, name: c.name, count: c.count })),
  ];

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: currentCategory ? `${currentCategory.name} - 韩影精选` : '分类浏览 - 韩影精选',
    description: currentCategory?.description || '韩国伦理电影分类浏览',
    url: `https://korean-film-review.readdy.ai/category/${activeSlug}`,
  };

  return (
    <>
      <ScrollToTop />
      <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>

      <Navbar />

      <main className="pt-20">
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-8">
          <div className="mb-6">
            <Breadcrumb items={breadcrumbItems} />
          </div>

          <h1 className="font-heading text-2xl md:text-4xl font-bold text-foreground-900 mb-2">
            {currentCategory ? currentCategory.name : t('nav.category')}
          </h1>
          {currentCategory && (
            <p className="text-foreground-600 mb-8">{currentCategory.description}</p>
          )}

          <div className="flex items-center gap-2 overflow-x-auto pb-4 mb-10 flex-wrap">
            {allTabs.map((tab) => (
              <Link
                key={tab.slug}
                to={`/category/${tab.slug}`}
                onClick={() => setActiveSlug(tab.slug)}
                className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all whitespace-nowrap cursor-pointer ${
                  activeSlug === tab.slug
                    ? 'bg-primary-500 text-background-50'
                    : 'bg-background-100 text-foreground-700 hover:bg-background-200'
                }`}
              >
                <span>{tab.name}</span>
                <span className={`text-xs px-2 py-0.5 rounded-full ${
                  activeSlug === tab.slug
                    ? 'bg-background-50/20 text-background-50'
                    : 'bg-background-200 text-foreground-500'
                }`}>
                  {tab.count}
                </span>
              </Link>
            ))}
          </div>

          {displayArticles.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
              {displayArticles.map((article) => (
                <ArticleCard key={article.id} article={article} />
              ))}
            </div>
          ) : (
            <div className="text-center py-20">
              <div className="w-20 h-20 mx-auto mb-6 flex items-center justify-center bg-background-100 rounded-full">
                <i className="ri-film-line text-3xl text-foreground-400"></i>
              </div>
              <p className="text-foreground-500 text-lg">该分类暂无文章，敬请期待</p>
            </div>
          )}
        </div>
      </main>

      <Footer />
    </>
  );
}